/**
 * image-utils — Préparation d'une image (capture d'écran) côté navigateur avant
 * de l'envoyer à Juliette (orbe → juliette-turn-process → Sonnet 4.6 multimodal).
 *
 * L'image est ÉPHÉMÈRE : on la compresse/redimensionne en data URL, on l'envoie
 * pour le tour en cours, puis on l'oublie (rien n'est stocké côté serveur).
 */

export const ACCEPTED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"] as const;

/** Taille max du fichier source accepté (avant compression). */
const MAX_INPUT_BYTES = 20 * 1024 * 1024; // 20 Mo
/** Côté le plus long après redimensionnement. */
const MAX_DIMENSION = 1600;
/** Qualité WebP de sortie. */
const OUTPUT_QUALITY = 0.82;
/** Garde-fou final : data URL résultante (le serveur refuse au-delà). */
const MAX_OUTPUT_DATAURL_CHARS = 5_000_000; // ~3,7 Mo réels

export interface PreparedImage {
  /** data URL complète : data:image/webp;base64,XXXX */
  dataUrl: string;
  mimeType: string;
  /** Taille approximative des octets réels (pour info/telemetrie). */
  sizeBytes: number;
}

export class ImageError extends Error {}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new ImageError("Image illisible."));
    img.src = src;
  });
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new ImageError("Lecture du fichier impossible."));
    reader.readAsDataURL(file);
  });
}

/**
 * Valide, redimensionne et compresse une image en WebP. Lève une ImageError
 * (message utilisateur) si le type/la taille ne conviennent pas.
 */
export async function prepareImageForJuliette(file: File): Promise<PreparedImage> {
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type as (typeof ACCEPTED_IMAGE_TYPES)[number])) {
    throw new ImageError("Format non supporté (PNG, JPEG ou WebP uniquement).");
  }
  if (file.size > MAX_INPUT_BYTES) {
    throw new ImageError("Image trop lourde (20 Mo max).");
  }

  const sourceUrl = await readAsDataUrl(file);
  const img = await loadImage(sourceUrl);

  let { width, height } = img;
  if (width === 0 || height === 0) {
    throw new ImageError("Image invalide.");
  }
  const scale = Math.min(1, MAX_DIMENSION / Math.max(width, height));
  width = Math.round(width * scale);
  height = Math.round(height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new ImageError("Compression impossible sur ce navigateur.");
  }
  ctx.drawImage(img, 0, 0, width, height);

  const dataUrl = canvas.toDataURL("image/webp", OUTPUT_QUALITY);
  if (!dataUrl.startsWith("data:image/webp")) {
    // Navigateur sans support WebP (rare) : repli JPEG.
    const jpeg = canvas.toDataURL("image/jpeg", OUTPUT_QUALITY);
    return finalize(jpeg, "image/jpeg");
  }
  return finalize(dataUrl, "image/webp");
}

function finalize(dataUrl: string, mimeType: string): PreparedImage {
  if (dataUrl.length > MAX_OUTPUT_DATAURL_CHARS) {
    throw new ImageError("Image trop détaillée — réessaie avec une capture plus petite.");
  }
  const base64Part = dataUrl.split(",")[1] ?? "";
  const sizeBytes = Math.round(base64Part.length * 0.75);
  return { dataUrl, mimeType, sizeBytes };
}
